import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
/** @jsx jsx */
/* eslint-disable @repo/internal/react/no-clone-element */
import React, { Component, Fragment } from 'react';
import { css, jsx } from '@emotion/react';
import { createAndFireEvent, withAnalyticsContext, withAnalyticsEvents } from '@atlaskit/analytics-next';
import toItemId from '../utils/to-item-id';
import Chevron from './internal/chevron';
import { TreeRowContainer } from './internal/styled';
const treeRowClickableStyles = css({
  cursor: 'pointer'
});
const packageName = "@atlaskit/table-tree";
const packageVersion = "9.6.10";
class Row extends Component {
  constructor(...args) {
    super(...args);
    _defineProperty(this, "state", {
      isExpanded: this.props.isDefaultExpanded || false
    });
    /**
     * This ensures a user won't trigger a click event and expand the accordion
     * when making a text selection.
     */
    _defineProperty(this, "onClickHandler", e => {
      var _window$getSelection;
      const selection = ((_window$getSelection = window.getSelection()) === null || _window$getSelection === void 0 ? void 0 : _window$getSelection.toString()) || '';
      if ((selection === null || selection === void 0 ? void 0 : selection.length) === 0) {
        this.onExpandToggle();
      }
    });
    _defineProperty(this, "onExpandToggle", () => {
      const {
        isExpanded
      } = this.props;
      if (isExpanded !== undefined) {
        this.onExpandStateChange(!isExpanded);
      } else {
        this.setState({
          isExpanded: !this.state.isExpanded
        });
        this.onExpandStateChange(!this.state.isExpanded);
      }
    });
    _defineProperty(this, "getExtendedLabel", (cellContent, cellIndex, mainColumnForExpandCollapseLabel) => {
      /**
       * First condition - when we pass data via `items` property in `<TableTree />`
       * Second condition - when we pass data via `<Rows />` as children in `<TableTree />`.
       */
      if (cellContent.hasOwnProperty('props')) {
        return cellContent === null || cellContent === void 0 ? void 0 : cellContent.props[mainColumnForExpandCollapseLabel === null || mainColumnForExpandCollapseLabel === void 0 ? void 0 : mainColumnForExpandCollapseLabel.toLowerCase()];
      } else if (cellIndex === mainColumnForExpandCollapseLabel) {
        return cellContent;
      }
      return undefined;
    });
  }
  componentDidUpdate(prevProps) {
    const {
      isDefaultExpanded,
      isExpanded
    } = this.props;
    if (isExpanded === undefined && isDefaultExpanded !== undefined && prevProps.isDefaultExpanded !== isDefaultExpanded && this.state.isExpanded !== isDefaultExpanded) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        isExpanded: isDefaultExpanded
      });
    }
  }
  onExpandStateChange(isExpanded) {
    if (this.props.data) {
      if (isExpanded && this.props.onExpand) {
        this.props.onExpand(this.props.data);
      } else if (!isExpanded && this.props.onCollapse) {
        this.props.onCollapse(this.props.data);
      }
    }
  }
  isExpanded() {
    const {
      isExpanded
    } = this.props;
    return isExpanded !== undefined ? isExpanded : this.state.isExpanded;
  }
  renderCell(cell, cellIndex) {
    const {
      props
    } = this;
    const isExpanded = this.isExpanded();
    const {
      hasChildren,
      depth,
      mainColumnForExpandCollapseLabel
    } = props;
    const isFirstCell = cellIndex === 0;
    const indentLevel = isFirstCell ? depth : 0;
    let cellContent = cell.props.children || [];
    const extendedLabel = this.getExtendedLabel(cellContent, cellIndex, mainColumnForExpandCollapseLabel);
    if (isFirstCell && hasChildren) {
      cellContent = [jsx(Chevron, {
        key: "chevron",
        expandLabel: props.expandLabel,
        collapseLabel: props.collapseLabel,
        extendedLabel: extendedLabel,
        isExpanded: isExpanded,
        onExpandToggle: this.onExpandToggle,
        ariaControls: isExpanded ? toItemId(props.itemId) : undefined,
        rowId: props.itemId
      })].concat(cellContent);
    }
    return /*#__PURE__*/React.cloneElement(cell, {
      key: cellIndex,
      columnIndex: cellIndex,
      indentLevel
    }, cellContent);
  }
  render() {
    const {
      shouldExpandOnClick,
      hasChildren,
      depth,
      renderChildren
    } = this.props;
    const isExpanded = this.isExpanded();
    const ariaAttrs = {};
    if (hasChildren) {
      ariaAttrs['aria-expanded'] = isExpanded;
    }
    if (depth !== undefined) {
      ariaAttrs['aria-level'] = depth;
    }
    return jsx(Fragment, null, jsx(TreeRowContainer, _extends({
      css: hasChildren && shouldExpandOnClick ? treeRowClickableStyles : undefined,
      onClick: hasChildren && shouldExpandOnClick ? this.onClickHandler : undefined
    }, ariaAttrs), React.Children.map(this.props.children, (cell, index) => this.renderCell(cell, index))), hasChildren && isExpanded && renderChildren && renderChildren());
  }
}
export { Row as RowWithoutAnalytics };
const createAndFireEventOnAtlaskit = createAndFireEvent('atlaskit');
export default withAnalyticsContext({
  componentName: 'row',
  packageName,
  packageVersion
})(withAnalyticsEvents({
  onExpand: createAndFireEventOnAtlaskit({
    action: 'expanded',
    actionSubject: 'tableTree',
    attributes: {
      componentName: 'row',
      packageName,
      packageVersion
    }
  }),
  onCollapse: createAndFireEventOnAtlaskit({
    action: 'collapsed',
    actionSubject: 'tableTree',
    attributes: {
      componentName: 'row',
      packageName,
      packageVersion
    }
  })
})(Row));